import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, useParams, useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';

function safeErrorMessage(error: unknown): string {
  if (error == null) return 'Erro desconhecido';
  if (typeof error === 'string') return error;
  if (isRouteErrorResponse(error)) return error.data?.message ?? error.statusText ?? `Erro ${error.status}`;
  if (error instanceof Error) return error.message;
  try {
    return JSON.stringify(error);
  } catch {
    return 'Erro desconhecido';
  }
}

function RouterErrorFallback() {
  const error = useRouteError();
  const message = safeErrorMessage(error);
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] p-4 text-center">
      <h1 className="text-xl font-semibold text-destructive mb-2">Algo deu errado</h1>
      <p className="text-muted-foreground mb-4 max-w-md">{message}</p>
      <Link to="/" className="text-primary hover:underline">Voltar ao início</Link>
    </div>
  );
}

function RedirectCustomerPlanToPlans() {
  const { customerId, planId } = useParams<{ customerId: string; planId: string }>();
  return <Navigate to={`/customers/${customerId}/plans/${planId}`} replace />;
}

function RedirectCustomerWorkoutToPlans() {
  const { customerId, planId, workoutId } = useParams<{ customerId: string; planId: string; workoutId: string }>();
  return <Navigate to={`/customers/${customerId}/plans/${planId}/${workoutId}`} replace />;
}

import { AppShell } from '@/src/AppShell';
import { PublicLayout } from '@/src/layouts/PublicLayout';
import { PrivateLayout } from '@/src/layouts/PrivateLayout';

const GA_ID = import.meta.env.VITE_GA_ID || 'G-XXXXXXXXXX';

const LoginPage = lazy(() => import('@/src/pages/LoginPage'));
const GoogleCallbackPage = lazy(() =>
  import('@/app/(public)/login/GoogleCallbackPage').then((m) => ({ default: m.default }))
);

const RegisterPage = lazy(() => import('@/app/(public)/register/page').then((m) => ({ default: m.default })));
const CheckEmailPage = lazy(() => import('@/app/(public)/check-email/page').then((m) => ({ default: m.default })));
const PasswordRecoveryPage = lazy(() => import('@/app/(public)/password/recovery/page').then((m) => ({ default: m.default })));
const PasswordResetPage = lazy(() => import('@/app/(public)/password/reset/page').then((m) => ({ default: m.default })));
const TermsPage = lazy(() => import('@/app/(public)/terms/page').then((m) => ({ default: m.default })));
const ValidationPage = lazy(() => import('@/app/(public)/validation/page').then((m) => ({ default: m.default })));

const DashboardPage = lazy(() => import('@/app/(private)/dashboard/page').then((m) => ({ default: m.default })));
const ProfilePage = lazy(() => import('@/app/(private)/profile/page').then((m) => ({ default: m.default })));
const PasswordPage = lazy(() => import('@/app/(private)/password/page').then((m) => ({ default: m.default })));
const TermsAgreementPage = lazy(() => import('@/app/(private)/terms/agreement/page').then((m) => ({ default: m.default })));
const InviteAcceptPage = lazy(() => import('@/app/(private)/invite/accept/page').then((m) => ({ default: m.default })));

const SelectCoachPage = lazy(() => import('@/app/(private)/(customer)/select-coach/page').then((m) => ({ default: m.default })));
const CoachesPage = lazy(() => import('@/app/(private)/(customer)/coaches/page').then((m) => ({ default: m.default })));
const CoachProfilePage = lazy(() => import('@/app/(private)/(customer)/coaches/[coachId]/profile/page').then((m) => ({ default: m.default })));
const CoachEvaluationsPage = lazy(() => import('@/app/(private)/(customer)/coaches/[coachId]/evaluations/page').then((m) => ({ default: m.default })));
const CustomerEvaluationsRedirectPage = lazy(() => import('@/app/(private)/(customer)/evaluations/page').then((m) => ({ default: m.default })));
const PlansPage = lazy(() => import('@/app/(private)/(customer)/plans/page').then((m) => ({ default: m.default })));
const PlansByCoachPage = lazy(() => import('@/app/(private)/(customer)/plans/[coachId]/page').then((m) => ({ default: m.default })));
const PlanDetailPage = lazy(() => import('@/app/(private)/(customer)/plans/[coachId]/[planId]/page').then((m) => ({ default: m.default })));
const WorkoutPage = lazy(() => import('@/app/(private)/(customer)/plans/[coachId]/[planId]/[workoutId]/page').then((m) => ({ default: m.default })));
const ExercisePage = lazy(() => import('@/app/(private)/(customer)/plans/[coachId]/[planId]/[workoutId]/[exerciseId]/page').then((m) => ({ default: m.default })));
const ExerciseEditPage = lazy(() => import('@/app/(private)/(customer)/plans/[coachId]/[planId]/[workoutId]/[exerciseId]/edit/page').then((m) => ({ default: m.default })));

const CustomersPage = lazy(() => import('@/app/(private)/(coach)/customers/page').then((m) => ({ default: m.default })));
const CustomerProfilePage = lazy(() => import('@/app/(private)/(coach)/customers/[customerId]/profile/page').then((m) => ({ default: m.default })));
const CustomerPlansPage = lazy(() => import('@/app/(private)/(coach)/customers/[customerId]/(plans)/page').then((m) => ({ default: m.default })));
const CustomerPlanDetailPage = lazy(() => import('@/app/(private)/(coach)/customers/[customerId]/(plans)/[planId]/page').then((m) => ({ default: m.default })));
const CustomerWorkoutPage = lazy(() => import('@/app/(private)/(coach)/customers/[customerId]/(plans)/[planId]/[workoutId]/page').then((m) => ({ default: m.default })));
const CustomerEvaluationsPage = lazy(() => import('@/app/(private)/(coach)/customers/[customerId]/evaluations/page').then((m) => ({ default: m.default })));
const NewEvaluationPage = lazy(() => import('@/app/(private)/(coach)/customers/[customerId]/evaluations/new/page').then((m) => ({ default: m.default })));

const ExercisesPage = lazy(() => import('@/app/(private)/(coach)/exercises/page').then((m) => ({ default: m.default })));
const ExerciseDetailPage = lazy(() => import('@/app/(private)/(coach)/exercises/[exerciseId]/page').then((m) => ({ default: m.default })));
const ExerciseEditPageCoach = lazy(() => import('@/app/(private)/(coach)/exercises/[exerciseId]/edit/page').then((m) => ({ default: m.default })));
const NewExercisePage = lazy(() => import('@/app/(private)/(coach)/exercises/new/page').then((m) => ({ default: m.default })));

const WorkoutPlansPage = lazy(() => import('@/app/(private)/(coach)/workout/plans/page').then((m) => ({ default: m.default })));
const NewWorkoutPlanPage = lazy(() => import('@/app/(private)/(coach)/workout/plans/new/page').then((m) => ({ default: m.default })));
const WorkoutPlanDetailPage = lazy(() => import('@/app/(private)/(coach)/workout/plans/[planId]/page').then((m) => ({ default: m.default })));
const EditWorkoutPlanPage = lazy(() => import('@/app/(private)/(coach)/workout/plans/[planId]/edit/page').then((m) => ({ default: m.default })));
const AssignPlanPage = lazy(() => import('@/app/(private)/(coach)/workout/plans/[planId]/assign/page').then((m) => ({ default: m.default })));
const NewWorkoutPage = lazy(() => import('@/app/(private)/(coach)/workout/plans/[planId]/workouts/new/page').then((m) => ({ default: m.default })));
const WorkoutDetailPage = lazy(() => import('@/app/(private)/(coach)/workout/plans/[planId]/workouts/[workoutId]/page').then((m) => ({ default: m.default })));
const EditWorkoutPage = lazy(() => import('@/app/(private)/(coach)/workout/plans/[planId]/workouts/[workoutId]/edit/page').then((m) => ({ default: m.default })));
const NewWorkoutExercisePage = lazy(() => import('@/app/(private)/(coach)/workout/plans/[planId]/workouts/[workoutId]/exercises/new/page').then((m) => ({ default: m.default })));
const WorkoutExercisePage = lazy(() => import('@/app/(private)/(coach)/workout/plans/[planId]/workouts/[workoutId]/exercises/[exerciseId]/page').then((m) => ({ default: m.default })));
const EditWorkoutExercisePage = lazy(() => import('@/app/(private)/(coach)/workout/plans/[planId]/workouts/[workoutId]/exercises/[exerciseId]/edit/page').then((m) => ({ default: m.default })));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function LazyPage({ Page }: { Page: React.LazyExoticComponent<React.ComponentType<any>> }) {
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-[200px]">Carregando...</div>}>
      <Page />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <AppShell gaId={GA_ID} />,
    errorElement: <RouterErrorFallback />,
    children: [
      { index: true, element: <Navigate to="/login" replace /> },
      {
        element: <PublicLayout />,
        children: [
      { path: 'login', element: <LazyPage Page={LoginPage} /> },
      { path: 'login/google', element: <LazyPage Page={GoogleCallbackPage} /> },
      { path: 'register', element: <LazyPage Page={RegisterPage} /> },
      { path: 'check-email', element: <LazyPage Page={CheckEmailPage} /> },
      { path: 'password/recovery', element: <LazyPage Page={PasswordRecoveryPage} /> },
      { path: 'password/reset', element: <LazyPage Page={PasswordResetPage} /> },
      { path: 'terms', element: <LazyPage Page={TermsPage} /> },
      { path: 'validation', element: <LazyPage Page={ValidationPage} /> },
        ],
      },
      {
        element: <PrivateLayout />,
    children: [
      { path: 'dashboard', element: <LazyPage Page={DashboardPage} /> },
      { path: 'profile', element: <LazyPage Page={ProfilePage} /> },
      { path: 'password', element: <LazyPage Page={PasswordPage} /> },
      { path: 'terms/agreement', element: <LazyPage Page={TermsAgreementPage} /> },
      { path: 'invite/accept', element: <LazyPage Page={InviteAcceptPage} /> },
      { path: 'select-coach', element: <LazyPage Page={SelectCoachPage} /> },
      { path: 'coaches', element: <LazyPage Page={CoachesPage} /> },
      { path: 'coaches/:coachId/profile', element: <LazyPage Page={CoachProfilePage} /> },
      { path: 'coaches/:coachId/evaluations', element: <LazyPage Page={CoachEvaluationsPage} /> },
      { path: 'evaluations', element: <LazyPage Page={CustomerEvaluationsRedirectPage} /> },
      { path: 'plans', element: <LazyPage Page={PlansPage} /> },
      { path: 'plans/:coachId', element: <LazyPage Page={PlansByCoachPage} /> },
      { path: 'plans/:coachId/:planId', element: <LazyPage Page={PlanDetailPage} /> },
      { path: 'plans/:coachId/:planId/:workoutId', element: <LazyPage Page={WorkoutPage} /> },
      { path: 'plans/:coachId/:planId/:workoutId/:exerciseId', element: <LazyPage Page={ExercisePage} /> },
      { path: 'plans/:coachId/:planId/:workoutId/:exerciseId/edit', element: <LazyPage Page={ExerciseEditPage} /> },
      { path: 'customers', element: <LazyPage Page={CustomersPage} /> },
      { path: 'customers/:customerId', element: <Navigate to="plans" replace /> },
      { path: 'customers/:customerId/profile', element: <LazyPage Page={CustomerProfilePage} /> },
      { path: 'customers/:customerId/plans', element: <LazyPage Page={CustomerPlansPage} /> },
      { path: 'customers/:customerId/plans/:planId', element: <LazyPage Page={CustomerPlanDetailPage} /> },
      { path: 'customers/:customerId/plans/:planId/:workoutId', element: <LazyPage Page={CustomerWorkoutPage} /> },
      { path: 'customers/:customerId/evaluations', element: <LazyPage Page={CustomerEvaluationsPage} /> },
      { path: 'customers/:customerId/evaluations/new', element: <LazyPage Page={NewEvaluationPage} /> },
      { path: 'customers/:customerId/:planId/:workoutId', element: <RedirectCustomerWorkoutToPlans /> },
      { path: 'customers/:customerId/:planId', element: <RedirectCustomerPlanToPlans /> },
      { path: 'exercises', element: <LazyPage Page={ExercisesPage} /> },
      { path: 'exercises/new', element: <LazyPage Page={NewExercisePage} /> },
      { path: 'exercises/:exerciseId', element: <LazyPage Page={ExerciseDetailPage} /> },
      { path: 'exercises/:exerciseId/edit', element: <LazyPage Page={ExerciseEditPageCoach} /> },
      { path: 'workout/plans', element: <LazyPage Page={WorkoutPlansPage} /> },
      { path: 'workout/plans/new', element: <LazyPage Page={NewWorkoutPlanPage} /> },
      { path: 'workout/plans/:planId', element: <LazyPage Page={WorkoutPlanDetailPage} /> },
      { path: 'workout/plans/:planId/edit', element: <LazyPage Page={EditWorkoutPlanPage} /> },
      { path: 'workout/plans/:planId/assign', element: <LazyPage Page={AssignPlanPage} /> },
      { path: 'workout/plans/:planId/workouts/new', element: <LazyPage Page={NewWorkoutPage} /> },
      { path: 'workout/plans/:planId/workouts/:workoutId', element: <LazyPage Page={WorkoutDetailPage} /> },
      { path: 'workout/plans/:planId/workouts/:workoutId/edit', element: <LazyPage Page={EditWorkoutPage} /> },
      { path: 'workout/plans/:planId/workouts/:workoutId/exercises/new', element: <LazyPage Page={NewWorkoutExercisePage} /> },
      { path: 'workout/plans/:planId/workouts/:workoutId/exercises/:exerciseId', element: <LazyPage Page={WorkoutExercisePage} /> },
      { path: 'workout/plans/:planId/workouts/:workoutId/exercises/:exerciseId/edit', element: <LazyPage Page={EditWorkoutExercisePage} /> },
        ],
      },
    ],
  },
]);
